import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell3 } from '@/components/app-shell-3';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { DndContext, DragOverlay, pointerWithin, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RotateCcw, Lock, Pencil, Check, FlaskConical, PanelRightOpen, PanelRightClose } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Import tile components
import { TileWrapper } from './TileWrapper';
import { ComplianceOverviewTile } from './tiles/ComplianceOverviewTile';
import { ExpiringSoonTile } from './tiles/ExpiringSoonTile';
import { ExpiredRequirementsTile } from './tiles/ExpiredRequirementsTile';
import { RequirementsByStatusTile } from './tiles/RequirementsByStatusTile';
import { MyTasksTile } from './tiles/MyTasksTile';
import { TasksDueSoonTile } from './tiles/TasksDueSoonTile';
import { OverdueTasksTile } from './tiles/OverdueTasksTile';
import { TaskProgressTile } from './tiles/TaskProgressTile';
import { TeamOverviewTile } from './tiles/TeamOverviewTile';
import { TeamDirectoryMiniTile } from './tiles/TeamDirectoryMiniTile';
import { NotificationsTile } from './tiles/NotificationsTile';
import { UnreadMessagesTile } from './tiles/UnreadMessagesTile';
import { QuickCheckInTile } from './tiles/QuickCheckInTile';
import { CreateTaskTile } from './tiles/CreateTaskTile';
import { MySpacesTile } from './tiles/MySpacesTile';
import { FavoriteSpacesTile } from './tiles/FavoriteSpacesTile';
import { TilePaletteSidebar, TILE_META as PALETTE_TILE_META } from './TilePaletteSidebar';

// Grid configuration
const GRID_COLS = 4;
const MIN_ROWS = 4;

// Size configurations - how many cells each size occupies
const SIZE_CELLS = {
  small: { cols: 1, rows: 1 },
  medium: { cols: 2, rows: 1 },
  tall: { cols: 1, rows: 2 },
  large: { cols: 2, rows: 2 },
  xl: { cols: 3, rows: 2 },
};

// Default tile configuration with FIXED positions
const DEFAULT_TILES = [
  { id: 'compliance-overview', type: 'compliance-overview', size: 'small', col: 0, row: 0, filter: {} },
  { id: 'expiring-soon', type: 'expiring-soon', size: 'small', col: 1, row: 0, filter: {} },
  { id: 'expired-requirements', type: 'expired-requirements', size: 'small', col: 2, row: 0, filter: {} },
  { id: 'requirements-status', type: 'requirements-status', size: 'small', col: 3, row: 0, filter: {} },
  { id: 'my-tasks', type: 'my-tasks', size: 'small', col: 0, row: 1, filter: {} },
  { id: 'tasks-due-soon', type: 'tasks-due-soon', size: 'small', col: 1, row: 1, filter: {} },
  { id: 'overdue-tasks', type: 'overdue-tasks', size: 'small', col: 2, row: 1, filter: {} },
  { id: 'task-progress', type: 'task-progress', size: 'small', col: 3, row: 1, filter: {} },
];

// Tile component mapping
const TILE_COMPONENTS = {
  'compliance-overview': ComplianceOverviewTile,
  'expiring-soon': ExpiringSoonTile,
  'expired-requirements': ExpiredRequirementsTile,
  'requirements-status': RequirementsByStatusTile,
  'my-tasks': MyTasksTile,
  'tasks-due-soon': TasksDueSoonTile,
  'overdue-tasks': OverdueTasksTile,
  'task-progress': TaskProgressTile,
  'team-overview': TeamOverviewTile,
  'team-directory': TeamDirectoryMiniTile,
  'notifications': NotificationsTile,
  'unread-messages': UnreadMessagesTile,
  'quick-checkin': QuickCheckInTile,
  'create-task': CreateTaskTile,
  'my-spaces': MySpacesTile,
  'favorite-spaces': FavoriteSpacesTile,
};

// Tile metadata for display
const TILE_META = {
  'compliance-overview': { title: 'Compliance Overview' },
  'expiring-soon': { title: 'Expiring Soon' },
  'expired-requirements': { title: 'Expired' },
  'requirements-status': { title: 'Requirements Status' },
  'my-tasks': { title: 'My Tasks' },
  'tasks-due-soon': { title: 'Due This Week' },
  'overdue-tasks': { title: 'Overdue' },
  'task-progress': { title: 'Task Progress' },
  'team-overview': { title: 'Team' },
  'team-directory': { title: 'Team Directory' },
  'notifications': { title: 'Notifications' },
  'unread-messages': { title: 'Messages' },
  'quick-checkin': { title: 'Check-In' },
  'create-task': { title: 'Quick Task' },
  'my-spaces': { title: 'My Spaces' },
  'favorite-spaces': { title: 'Favorites' },
};

// Access denied component
const AccessDenied = () => (
  <AppShell3 pageTitle="Access Denied" pageDescription="You don't have access to this page">
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="bg-red-100 p-6 rounded-full mb-6">
        <Lock className="h-12 w-12 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
      <p className="text-gray-600 max-w-md">
        This dashboard test page is restricted to authorized users only.
      </p>
    </div>
  </AppShell3>
);

// Droppable cell component
function DroppableCell({ col, row }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${col}-${row}`,
    data: { col, row },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "aspect-square rounded-lg border-2 border-dashed transition-colors duration-150",
        isOver
          ? "border-primary bg-primary/20"
          : "border-gray-200 bg-gray-50/50 hover:border-gray-300"
      )}
      style={{
        gridColumn: col + 1,
        gridRow: row + 1,
      }}
    />
  );
}

// Drag overlay preview
function TilePreview({ tileType, size }) {
  const meta = PALETTE_TILE_META[tileType];
  const Icon = meta?.icon;
  const sizeConfig = SIZE_CELLS[size] || SIZE_CELLS.small;

  return (
    <Card className="p-4 shadow-xl opacity-90 pointer-events-none" style={{
      width: sizeConfig.cols * 120,
      height: sizeConfig.rows * 120,
    }}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
        <div>
          <p className="font-medium text-sm">{meta?.title || tileType}</p>
          <p className="text-xs text-gray-500 capitalize">{size}</p>
        </div>
      </div>
    </Card>
  );
}

export default function DashboardTestPage() {
  const { user, userProfile, company } = useAuth();
  const [tiles, setTiles] = useState(DEFAULT_TILES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [useMockData, setUseMockData] = useState(false);

  const hasAccess = user?.email === 'tom@bohsa.co.uk';

  // Build occupancy map - which cells are occupied by which tile
  const { occupiedCells, gridRows } = useMemo(() => {
    const occupied = new Map(); // cell key -> tile id
    let maxRow = MIN_ROWS;

    for (const tile of tiles) {
      if (typeof tile.col !== 'number' || typeof tile.row !== 'number') continue;

      const sizeConfig = SIZE_CELLS[tile.size] || SIZE_CELLS.small;
      for (let c = 0; c < sizeConfig.cols; c++) {
        for (let r = 0; r < sizeConfig.rows; r++) {
          occupied.set(`${tile.col + c}-${tile.row + r}`, tile.id);
        }
      }
      maxRow = Math.max(maxRow, tile.row + sizeConfig.rows);
    }

    // Add extra rows when dragging for more drop targets
    const rows = isDragging ? Math.max(maxRow + 2, MIN_ROWS + 2) : Math.max(maxRow, MIN_ROWS);

    return { occupiedCells: occupied, gridRows: rows };
  }, [tiles, isDragging]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Ensure tiles have valid positions (migration helper)
  const ensurePositions = useCallback((tilesArray) => {
    const occupied = new Set();
    const result = [];

    for (const tile of tilesArray) {
      // If tile has valid position, use it
      if (typeof tile.col === 'number' && typeof tile.row === 'number') {
        const sizeConfig = SIZE_CELLS[tile.size] || SIZE_CELLS.small;
        for (let c = 0; c < sizeConfig.cols; c++) {
          for (let r = 0; r < sizeConfig.rows; r++) {
            occupied.add(`${tile.col + c}-${tile.row + r}`);
          }
        }
        result.push(tile);
      } else {
        // Find first available position
        const sizeConfig = SIZE_CELLS[tile.size] || SIZE_CELLS.small;
        let placed = false;

        for (let row = 0; row < 20 && !placed; row++) {
          for (let col = 0; col <= GRID_COLS - sizeConfig.cols && !placed; col++) {
            let fits = true;
            for (let c = 0; c < sizeConfig.cols && fits; c++) {
              for (let r = 0; r < sizeConfig.rows && fits; r++) {
                if (occupied.has(`${col + c}-${row + r}`)) fits = false;
              }
            }
            if (fits) {
              for (let c = 0; c < sizeConfig.cols; c++) {
                for (let r = 0; r < sizeConfig.rows; r++) {
                  occupied.add(`${col + c}-${row + r}`);
                }
              }
              result.push({ ...tile, col, row });
              placed = true;
            }
          }
        }
      }
    }
    return result;
  }, []);

  // Load dashboard config
  useEffect(() => {
    if (!hasAccess || !userProfile?.id) return;

    const loadConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('dashboard_configs')
          .select('*')
          .eq('user_id', userProfile.id)
          .eq('config_name', 'default')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading dashboard config:', error);
        }

        if (data?.tiles && Array.isArray(data.tiles) && data.tiles.length > 0) {
          const positioned = ensurePositions(data.tiles);
          setTiles(positioned);
        }
      } catch (err) {
        console.error('Failed to load config:', err);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [hasAccess, userProfile?.id, ensurePositions]);

  // Load spaces
  useEffect(() => {
    if (!hasAccess || !company?.id) return;

    const loadSpaces = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_archived', false)
        .order('name');

      if (!error && data) {
        setSpaces(data);
      }
    };

    loadSpaces();
  }, [hasAccess, company?.id]);

  // Save config
  const saveConfig = useCallback(async (newTiles) => {
    if (!userProfile?.id) return;

    setSaving(true);
    try {
      await supabase
        .from('dashboard_configs')
        .upsert({
          user_id: userProfile.id,
          config_name: 'default',
          tiles: newTiles,
          updated_date: new Date().toISOString(),
        }, {
          onConflict: 'user_id,config_name',
        });
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error('Failed to save dashboard layout');
    } finally {
      setSaving(false);
    }
  }, [userProfile?.id]);

  // Check if a position is valid for a tile (no overlap with other tiles)
  const canPlaceTile = useCallback((col, row, size, excludeTileId = null) => {
    const sizeConfig = SIZE_CELLS[size] || SIZE_CELLS.small;

    // Check bounds
    if (col < 0 || col + sizeConfig.cols > GRID_COLS) return false;
    if (row < 0) return false;

    // Check each cell the tile would occupy
    for (let c = 0; c < sizeConfig.cols; c++) {
      for (let r = 0; r < sizeConfig.rows; r++) {
        const cellKey = `${col + c}-${row + r}`;
        const occupyingTile = occupiedCells.get(cellKey);
        if (occupyingTile && occupyingTile !== excludeTileId) {
          return false;
        }
      }
    }
    return true;
  }, [occupiedCells]);

  // Handle filter change
  const handleFilterChange = useCallback((tileId, filter) => {
    setTiles(items => {
      const newItems = items.map(item =>
        item.id === tileId ? { ...item, filter } : item
      );
      saveConfig(newItems);
      return newItems;
    });
  }, [saveConfig]);

  // Find first available position for a tile, avoiding occupied cells
  const findAvailablePosition = useCallback((size, occupiedMap, excludeTileId = null) => {
    const sizeConfig = SIZE_CELLS[size] || SIZE_CELLS.small;

    for (let row = 0; row < 50; row++) {
      for (let col = 0; col <= GRID_COLS - sizeConfig.cols; col++) {
        let fits = true;
        for (let c = 0; c < sizeConfig.cols && fits; c++) {
          for (let r = 0; r < sizeConfig.rows && fits; r++) {
            const key = `${col + c}-${row + r}`;
            const occupier = occupiedMap.get(key);
            if (occupier && occupier !== excludeTileId) {
              fits = false;
            }
          }
        }
        if (fits) {
          return { col, row };
        }
      }
    }
    return null;
  }, []);

  // Handle size change - push other tiles out of the way if needed
  const handleSizeChange = useCallback((tileId, newSize) => {
    setTiles(items => {
      const tile = items.find(t => t.id === tileId);
      if (!tile) return items;

      const newSizeConfig = SIZE_CELLS[newSize] || SIZE_CELLS.small;

      // Build occupancy map excluding the resizing tile
      const occupied = new Map();
      for (const t of items) {
        if (t.id === tileId) continue;
        if (typeof t.col !== 'number' || typeof t.row !== 'number') continue;
        const sc = SIZE_CELLS[t.size] || SIZE_CELLS.small;
        for (let c = 0; c < sc.cols; c++) {
          for (let r = 0; r < sc.rows; r++) {
            occupied.set(`${t.col + c}-${t.row + r}`, t.id);
          }
        }
      }

      // Find tiles that would be displaced by the new size
      const displacedTileIds = new Set();
      for (let c = 0; c < newSizeConfig.cols; c++) {
        for (let r = 0; r < newSizeConfig.rows; r++) {
          const key = `${tile.col + c}-${tile.row + r}`;
          const occupier = occupied.get(key);
          if (occupier) {
            displacedTileIds.add(occupier);
          }
        }
      }

      // If no tiles displaced, just resize
      if (displacedTileIds.size === 0) {
        const newItems = items.map(item =>
          item.id === tileId ? { ...item, size: newSize } : item
        );
        saveConfig(newItems);
        return newItems;
      }

      // Try to relocate displaced tiles
      // First, build a new occupancy map with the resized tile
      const newOccupied = new Map();
      for (let c = 0; c < newSizeConfig.cols; c++) {
        for (let r = 0; r < newSizeConfig.rows; r++) {
          newOccupied.set(`${tile.col + c}-${tile.row + r}`, tileId);
        }
      }

      // Add non-displaced tiles to occupied map
      for (const t of items) {
        if (t.id === tileId || displacedTileIds.has(t.id)) continue;
        if (typeof t.col !== 'number' || typeof t.row !== 'number') continue;
        const sc = SIZE_CELLS[t.size] || SIZE_CELLS.small;
        for (let c = 0; c < sc.cols; c++) {
          for (let r = 0; r < sc.rows; r++) {
            newOccupied.set(`${t.col + c}-${t.row + r}`, t.id);
          }
        }
      }

      // Try to find new positions for displaced tiles
      const relocations = new Map();
      for (const displacedId of displacedTileIds) {
        const displacedTile = items.find(t => t.id === displacedId);
        if (!displacedTile) continue;

        const newPos = findAvailablePosition(displacedTile.size, newOccupied, displacedId);
        if (!newPos) {
          toast.error('Not enough space to rearrange tiles');
          return items;
        }

        relocations.set(displacedId, newPos);

        // Add relocated tile to occupied map
        const sc = SIZE_CELLS[displacedTile.size] || SIZE_CELLS.small;
        for (let c = 0; c < sc.cols; c++) {
          for (let r = 0; r < sc.rows; r++) {
            newOccupied.set(`${newPos.col + c}-${newPos.row + r}`, displacedId);
          }
        }
      }

      // Apply all changes
      const newItems = items.map(item => {
        if (item.id === tileId) {
          return { ...item, size: newSize };
        }
        if (relocations.has(item.id)) {
          const { col, row } = relocations.get(item.id);
          return { ...item, col, row };
        }
        return item;
      });

      saveConfig(newItems);
      toast.success(`Resized and moved ${relocations.size} tile${relocations.size > 1 ? 's' : ''}`);
      return newItems;
    });
  }, [saveConfig, findAvailablePosition]);

  // Handle reset
  const handleReset = useCallback(() => {
    setTiles(DEFAULT_TILES);
    saveConfig(DEFAULT_TILES);
    toast.success('Dashboard reset to default layout');
  }, [saveConfig]);

  // Handle add tile at specific position
  const handleAddTile = useCallback((tileType, targetCol = null, targetRow = null) => {
    const meta = PALETTE_TILE_META[tileType];
    const size = meta?.defaultSize || 'small';
    const sizeConfig = SIZE_CELLS[size];

    // If no target specified, find first available position
    let col = targetCol;
    let row = targetRow;

    if (col === null || row === null) {
      // Find first available position
      let found = false;
      for (let r = 0; r < 20 && !found; r++) {
        for (let c = 0; c <= GRID_COLS - sizeConfig.cols && !found; c++) {
          if (canPlaceTile(c, r, size)) {
            col = c;
            row = r;
            found = true;
          }
        }
      }
      if (!found) {
        toast.error('No space available for this tile');
        return;
      }
    }

    const newTile = {
      id: `${tileType}-${Date.now()}`,
      type: tileType,
      size,
      col,
      row,
      filter: {},
    };

    setTiles(items => {
      const newItems = [...items, newTile];
      saveConfig(newItems);
      return newItems;
    });

    toast.success(`Added ${meta?.title || tileType}`);
  }, [saveConfig, canPlaceTile]);

  // Handle remove tile
  const handleRemoveTile = useCallback((tileId) => {
    setTiles(items => {
      const newItems = items.filter(item => item.id !== tileId);
      saveConfig(newItems);
      return newItems;
    });
    toast.success('Tile removed');
  }, [saveConfig]);

  // Handle drag start
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setIsDragging(true);

    if (active.data.current?.fromSidebar) {
      setActiveData({
        type: 'sidebar',
        tileType: active.data.current.tileType,
        size: PALETTE_TILE_META[active.data.current.tileType]?.defaultSize || 'small',
      });
    } else {
      const tile = tiles.find(t => t.id === active.id);
      if (tile) {
        setActiveData({
          type: 'tile',
          tileId: active.id,
          tileType: tile.type,
          size: tile.size,
        });
      }
    }
  }, [tiles]);

  // Handle drag end
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    setIsDragging(false);
    setActiveData(null);

    if (!over) return;

    // Parse drop target
    const overId = over.id?.toString();
    if (!overId?.startsWith('cell-')) return;

    const [, colStr, rowStr] = overId.split('-');
    const targetCol = parseInt(colStr, 10);
    const targetRow = parseInt(rowStr, 10);

    // Adding from sidebar
    if (active.data.current?.fromSidebar) {
      const tileType = active.data.current.tileType;
      const size = PALETTE_TILE_META[tileType]?.defaultSize || 'small';

      if (canPlaceTile(targetCol, targetRow, size)) {
        handleAddTile(tileType, targetCol, targetRow);
      } else {
        toast.error('Cannot place tile here - overlaps with existing tiles');
      }
      return;
    }

    // Moving existing tile
    const tileId = active.id;
    const tile = tiles.find(t => t.id === tileId);
    if (!tile) return;

    // Check if can place at new position (excluding self)
    if (!canPlaceTile(targetCol, targetRow, tile.size, tileId)) {
      toast.error('Cannot move here - overlaps with existing tiles');
      return;
    }

    // Update tile position
    setTiles(items => {
      const newItems = items.map(item =>
        item.id === tileId
          ? { ...item, col: targetCol, row: targetRow }
          : item
      );
      saveConfig(newItems);
      return newItems;
    });
  }, [tiles, canPlaceTile, handleAddTile, saveConfig]);

  if (!hasAccess) {
    return <AccessDenied />;
  }

  // Generate all empty cells for the grid
  const emptyCells = [];
  if (isDragging) {
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const cellKey = `${col}-${row}`;
        // Only render empty cell if not occupied
        if (!occupiedCells.has(cellKey)) {
          emptyCells.push(
            <DroppableCell key={`empty-${cellKey}`} col={col} row={row} />
          );
        }
      }
    }
  }

  return (
    <AppShell3
      pageTitle="Dashboard Test"
      pageDescription="Drag-and-drop dashboard builder"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={cn("container mx-auto px-4 py-6 transition-all", sidebarOpen && "pr-[340px]")}>
          {/* Header Actions */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-500">
                {saving ? 'Saving...' : isDragging ? 'Drop on an empty cell to place tile' : 'Drag tiles to reposition. Changes auto-save.'}
              </p>
              {/* Mock Data Toggle */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <FlaskConical className="h-4 w-4 text-amber-600" />
                <Label htmlFor="mock-data" className="text-sm text-amber-700 cursor-pointer">
                  Simulate Data
                </Label>
                <Switch
                  id="mock-data"
                  checked={useMockData}
                  onCheckedChange={setUseMockData}
                  className="data-[state=checked]:bg-amber-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button
                variant={isEditMode ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (isEditMode) {
                    setSidebarOpen(false);
                  }
                  setIsEditMode(!isEditMode);
                }}
              >
                {isEditMode ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Done
                  </>
                ) : (
                  <>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </>
                )}
              </Button>
            </div>

          {/* Sidebar Toggle Tab - fixed to right edge */}
          {isEditMode && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={cn(
                "fixed right-0 top-1/2 -translate-y-1/2 z-40",
                "flex items-center gap-2 px-2 py-4",
                "bg-primary text-white rounded-l-lg shadow-lg",
                "hover:bg-primary/90 transition-all",
                sidebarOpen && "right-80"
              )}
            >
              {sidebarOpen ? (
                <PanelRightClose className="h-5 w-5" />
              ) : (
                <>
                  <PanelRightOpen className="h-5 w-5" />
                  <span className="text-sm font-medium [writing-mode:vertical-lr] rotate-180">Add Tiles</span>
                </>
              )}
            </button>
          )}
          </div>

          {/* Tile Grid */}
          {loading ? (
            <div className="grid grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div
              className={cn(
                "grid gap-3 p-4 rounded-lg transition-[border,background-color] duration-200",
                (isEditMode || isDragging) && "border-2 border-dashed border-gray-300 bg-gray-50/30"
              )}
              style={{
                gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
              }}
            >
              {/* Empty drop target cells - shown when dragging */}
              {emptyCells}

              {/* Tiles with fixed positions */}
              {tiles.map((tile) => {
                const TileComponent = TILE_COMPONENTS[tile.type];
                const meta = TILE_META[tile.type];

                if (!TileComponent) return null;
                if (typeof tile.col !== 'number' || typeof tile.row !== 'number') return null;

                return (
                  <TileWrapper
                    key={tile.id}
                    id={tile.id}
                    title={meta?.title || tile.type}
                    size={tile.size}
                    col={tile.col}
                    row={tile.row}
                    filter={tile.filter}
                    spaces={spaces}
                    onFilterChange={(filter) => handleFilterChange(tile.id, filter)}
                    onSizeChange={(size) => handleSizeChange(tile.id, size)}
                    isEditMode={isEditMode}
                    onRemove={() => handleRemoveTile(tile.id)}
                  >
                    <TileComponent
                      filter={tile.filter}
                      size={tile.size}
                      useMockData={useMockData}
                    />
                  </TileWrapper>
                );
              })}

              {/* Empty state */}
              {tiles.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400">
                  <p className="text-lg mb-2">No tiles added yet</p>
                  <p className="text-sm">Click Edit to add tiles from the sidebar</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tile Palette Sidebar */}
        <TilePaletteSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeTileTypes={tiles.map(t => t.type)}
          onAddTile={handleAddTile}
        />

        {/* Drag Overlay */}
        <DragOverlay>
          {activeData && (
            <TilePreview
              tileType={activeData.tileType}
              size={activeData.size}
            />
          )}
        </DragOverlay>
      </DndContext>
    </AppShell3>
  );
}
